import { useState } from 'react'
import { Check, Lock, MessagesSquare, Plus } from 'lucide-react'
import { useThreads } from '../hooks'
import type { Thread, ThreadStatus } from '../types'

type StatusFilter = 'OPEN' | 'RESOLVED' | 'ALL'

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'OPEN', label: 'Otwarte' },
  { key: 'RESOLVED', label: 'Rozwiązane' },
  { key: 'ALL', label: 'Wszystkie' },
]

export function ThreadList({ entityRef, selectedId, onSelect, onNewThread }: {
  entityRef: string
  selectedId: number | null
  onSelect: (thread: Thread) => void
  onNewThread: () => void
}) {
  const [filter, setFilter] = useState<StatusFilter>('OPEN')
  const status: ThreadStatus | undefined = filter === 'ALL' ? undefined : filter
  const { data: threads, isLoading } = useThreads(entityRef, status)
  const empty = !isLoading && (threads?.length ?? 0) === 0

  return (
    <aside className="flex h-full w-full flex-col border-r border-slate-200 bg-slate-50/80">
      <header className="space-y-2 border-b border-slate-200 px-3 py-2.5">
        <button
          onClick={onNewThread}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-sky-600 px-2.5 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-sky-700"
        >
          <Plus className="h-3.5 w-3.5" />
          Nowy wątek
        </button>
        <div className="flex gap-0.5 rounded-lg bg-slate-200/70 p-0.5 text-xs">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={[
                'flex-1 rounded-md px-2 py-1 font-medium transition-colors',
                filter === f.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading && <p className="px-3 py-4 text-xs text-slate-400">Ładowanie…</p>}

        {empty && (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <MessagesSquare className="h-7 w-7 text-slate-300" />
            <p className="text-xs text-slate-400">Brak wątków w tym widoku</p>
          </div>
        )}

        {threads?.map(t => {
          const resolved = t.status === 'RESOLVED'
          const selected = selectedId === t.id
          return (
            <button
              key={t.id}
              type="button"
              data-thread-id={t.id}
              onClick={() => onSelect(t)}
              className={[
                'flex w-full items-center gap-2 border-b border-slate-100 border-l-2 px-3 py-2.5 text-left text-sm transition-colors',
                selected
                  ? 'border-l-sky-600 bg-white'
                  : 'border-l-transparent hover:bg-white/70',
              ].join(' ')}
            >
              {resolved && (
                <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="Rozwiązany" />
              )}
              <span
                className={[
                  'min-w-0 flex-1 truncate',
                  resolved ? 'text-slate-400 line-through' : 'font-medium text-slate-700',
                ].join(' ')}
              >
                {t.title}
              </span>
              {t.visibility === 'PRIVATE' && (
                <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-label="Prywatny" />
              )}
            </button>
          )
        })}
      </div>
    </aside>
  )
}
